import type { IEwdRulesetRestPeriodConfig, IEwdRulesetWorkPeriodConfig, IEwdWorkRestAvailability, IHosRuleset } from "../interfaces/hosRuleset";
import type { IViolation } from "../interfaces/violation";
import {
    HOURS,
    DAYS,
    type IEwdState,
    type IWorkRestCount,
    type IEwdWorkPeriod,
    type IEwdRestPeriod,
    type IRestSegment,
    type IValidation,
    getDefaultAvailability,
    msToTimespan,
    type ICalculationResult
} from "./helpers";

let addViolationLimit = (violation: IViolation, timePeriod: number) => {
    if (timePeriod <= 24 * HOURS) {
        violation.hoursLimit = timePeriod / HOURS;
    } else {
        violation.daysLimit = timePeriod / DAYS;
    }
};

let calculateViolation = (state: IEwdState) => {
        if (state.current.status !== "Work" && state.current.status !== "WorkExemption") {
            return;
        }

        state.timePeriodList.forEach(timePeriodKey => {
            let currentTimePeriod = state.workRestCounts[timePeriodKey];
            let workAvailability, timeUntilMustRest;
            if (currentTimePeriod.restPeriod && currentTimePeriod.restPeriod.restMinimum && currentTimePeriod.restPeriod.countRequired) {
                timeUntilMustRest = getTimeUntilRestDue(state, currentTimePeriod);
            }
            if (currentTimePeriod.workPeriod && currentTimePeriod.workPeriod.workMaximum && currentTimePeriod.workPeriod.workCount) {
                workAvailability = currentTimePeriod.workPeriod.workMaximum - currentTimePeriod.workPeriod.workCount;
            }
            if (timeUntilMustRest && timeUntilMustRest < 0) {
                let violation: IViolation = {
                    fromDate: new Date(state.nextDate.getTime() + timeUntilMustRest),
                    toDate: state.nextDate,
                    logId: state.current.id,
                    type: "EwdRest"
                };
                addViolationLimit(violation, timePeriodKey);
                state.violations.push(violation);
            }
            if (workAvailability && workAvailability < 0) {
                let violation: IViolation = {
                    fromDate: new Date(state.nextDate.getTime() + workAvailability),
                    toDate: state.nextDate,
                    logId: state.current.id,
                    type: "EwdWork"
                };
                addViolationLimit(violation, timePeriodKey);
                state.violations.push(violation);
            }
        });
    },
    calculateAvailability = (state: IEwdState, validationResults: IValidation) => {
        let availabilityCollection: IEwdWorkRestAvailability[] = [];
        state.timePeriodList.forEach(timePeriodKey => {
            let currentTimePeriod = state.workRestCounts[timePeriodKey];
            let availability: IEwdWorkRestAvailability = {
                timePeriod: timePeriodKey,
                startDate: currentTimePeriod.startDate,
                toDate: currentTimePeriod.toDate
            };

            if (state.consecutiveRestCount > 0) {
                availability.rested = msToTimespan(state.consecutiveRestCount);
            }

            let timeUntilMustRest = getTimeUntilRestDue(state, currentTimePeriod);
            if (currentTimePeriod.workPeriod && currentTimePeriod.workPeriod.workMaximum && currentTimePeriod.workPeriod.workCount !== undefined) {
                let workAvailability = currentTimePeriod.workPeriod.workMaximum - currentTimePeriod.workPeriod.workCount;
                if (timeUntilMustRest !== undefined) {
                    timeUntilMustRest = Math.max(Math.min(timeUntilMustRest, workAvailability), 0);
                } else {
                    timeUntilMustRest = Math.max(workAvailability, 0);
                }
            }
            if (currentTimePeriod.restPeriod && currentTimePeriod.restPeriod.nightRestBreakCount !== undefined) {
                availability.nightRestBreakCount = currentTimePeriod.restPeriod.nightRestBreakCount;
            }
            if (currentTimePeriod.restPeriod && currentTimePeriod.restPeriod.consecutiveNightRestBreakCount !== undefined) {
                availability.consecutiveNightRestBreakCount = currentTimePeriod.restPeriod.consecutiveNightRestBreakCount;
            }
            availability.timeUntilMustRest = timeUntilMustRest !== undefined ? msToTimespan(timeUntilMustRest) : undefined;
            availabilityCollection.push(availability);
        });
        validationResults.ewdAvailability = availabilityCollection;
        return validationResults;
    },
    getTimeUntilRestDue = (state: IEwdState, currentTimePeriod: IWorkRestCount) => {
        if (
            currentTimePeriod.restPeriod === undefined ||
            currentTimePeriod.restPeriod.restMinimum === undefined ||
            currentTimePeriod.restPeriod.countRequired === undefined ||
            currentTimePeriod.restPeriod.restSegments === undefined
        ) {
            return undefined;
        }

        let unfinishedSegments = currentTimePeriod.restPeriod.countRequired - currentTimePeriod.restPeriod.restSegments.length;
        let unfinishedRest = currentTimePeriod.restPeriod.restMinimum * Math.max(unfinishedSegments, 0);

        let index = 0;
        for (let i = 0; i < state.logs.length; i++) {
            if (state.logs[i].id === state.current.id) {
                index = i;
                break;
            }
        }

        // Pause "Time Until <X Length> Rest" while in Rest.
        if (state.current.status === "Rest" && state.current.dateTime === state.logs[index].dateTime && unfinishedSegments > 0) {
            return currentTimePeriod.toDate.getTime() - state.currentDate.getTime() - unfinishedRest;
        }

        return currentTimePeriod.toDate.getTime() - state.nextDate.getTime() - unfinishedRest;
    },
    addDummyLog = (state: IEwdState, dummyLogDate: Date | undefined) => {
        // Create a dummy log that is a duplicate of the most recent status log, with the provided dummyLogDate
        if (dummyLogDate === undefined && state.logs.length > 1) {
            return;
        }
        let prevLog = state.logs.length ? state.logs[state.logs.length - 1] : state.next;
        if (prevLog) {
            let logDate = dummyLogDate === undefined ? prevLog.dateTime : dummyLogDate;
            let dummyLog = JSON.parse(JSON.stringify(prevLog));
            dummyLog.dateTime = logDate;
            dummyLog.dummyLog = true;
            state.logs.push(dummyLog);
        }
    },
    countWorkTime = (state: IEwdState) => {
        state.workExemptionCount = 0;
        state.consecutiveRestCount = 0;

        state.timePeriodList.forEach(timePeriod => {
            let workPeriod = state.workRestCounts[timePeriod].workPeriod;
            if (workPeriod === undefined) {
                return;
            }
            if (workPeriod.workCount !== undefined) {
                workPeriod.workCount += state.duration;
            }
        });
    },
    /**
     * Calculates & returns the absolute number of days between two dates.
     */
    getDaysBetween = (startDate: Date, endDate: Date) => {
        let start = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        let end = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        if (start > end) {
            return -1;
        }

        return (end - start) / (24 * HOURS);
    },
    // checks # of 7 hour consecutive rest from 10PM to 8AM
    getNightRestBreakCountBy7HourRest = (state: IEwdState) => {
        let nightRestBreakCount: number | undefined;
        if (state.duration < 7 * HOURS) {
            nightRestBreakCount = 0;
        } else if (state.currentDate.getHours() <= 1) {
            nightRestBreakCount = getDaysBetween(state.currentDate, state.nextDate) + 1;
        } else if (state.currentDate.getHours() <= 22 && state.nextDate.getHours() >= 5) {
            nightRestBreakCount = getDaysBetween(state.currentDate, state.nextDate);
        }
        return nightRestBreakCount;
    },
    // checks # of 24 hour consecutive rest
    getNightRestBreakCountBy24HourRest = (state: IEwdState) => {
        if (state.duration < 24 * HOURS) {
            return 0;
        }
        return getDaysBetween(state.currentDate, state.nextDate);
    },
    checkForNightRestBreak = (state: IEwdState, currentRestPeriod: IEwdRestPeriod) => {
        // Math.max(undefined, n) is NaN, which fails the "> 0" check below; keep that behaviour with an explicit NaN.
        let restMeetNightRestBreakCount = Math.max(getNightRestBreakCountBy7HourRest(state) ?? NaN, getNightRestBreakCountBy24HourRest(state));
        if (restMeetNightRestBreakCount > 0 && currentRestPeriod.nightRestBreakCount !== undefined) {
            currentRestPeriod.nightRestBreakCount += restMeetNightRestBreakCount;
            // check for consecutive NightRestBreak
            if (currentRestPeriod.consecutiveNightRestBreakCount !== undefined) {
                let isLastNightRestBreakYesterday =
                    state.lastNightRestBreakDate &&
                    state.nextDate.getTime() > state.lastNightRestBreakDate.getTime() &&
                    getDaysBetween(state.lastNightRestBreakDate, state.nextDate) === 1;
                // Case #1: Last NightRestBreak was on yesterday
                if (isLastNightRestBreakYesterday) {
                    currentRestPeriod.consecutiveNightRestBreakCount =
                        Math.max(currentRestPeriod.consecutiveNightRestBreakCount, 1) + restMeetNightRestBreakCount;
                }
                // Case #2: Current rest qualify for multiple NightRestBreak
                if (restMeetNightRestBreakCount >= 2) {
                    currentRestPeriod.consecutiveNightRestBreakCount += restMeetNightRestBreakCount;
                }
            }
            state.lastNightRestBreakDate = state.nextDate;
        }
    },
    isTimePeriodReset = (currentRestPeriod: IEwdRestPeriod) => {
        let isRestCompleted, isNightRestBreakCompleted, isConsecutiveNightRestBreakCompleted;
        isRestCompleted = isNightRestBreakCompleted = isConsecutiveNightRestBreakCompleted = true;

        // If rest info all defined, check if it meets the required amount
        if (currentRestPeriod && currentRestPeriod.restMinimum && currentRestPeriod.countRequired && currentRestPeriod.restSegments !== undefined) {
            isRestCompleted = currentRestPeriod.restSegments.length >= currentRestPeriod.countRequired;
        }

        // If NightRestBreak defined, check if it also meets the required amount
        if (currentRestPeriod.nightRestBreakCount !== undefined && currentRestPeriod.nightRestBreakCountRequired !== undefined) {
            isNightRestBreakCompleted = currentRestPeriod.nightRestBreakCount >= currentRestPeriod.nightRestBreakCountRequired;
        }
        if (
            currentRestPeriod.consecutiveNightRestBreakCount !== undefined &&
            currentRestPeriod.consecutiveNightRestBreakCountRequired !== undefined
        ) {
            isConsecutiveNightRestBreakCompleted =
                currentRestPeriod.consecutiveNightRestBreakCount >= currentRestPeriod.consecutiveNightRestBreakCountRequired;
        }
        // If all condition meet, reset this time period
        return isRestCompleted && isNightRestBreakCompleted && isConsecutiveNightRestBreakCompleted;
    },
    countRestTime = (state: IEwdState) => {
        state.workExemptionCount = 0;
        state.consecutiveRestCount += state.duration;

        state.timePeriodList.forEach(timePeriod => {
            let restPeriod = state.workRestCounts[timePeriod].restPeriod;
            let workPeriod = state.workRestCounts[timePeriod].workPeriod;
            if (restPeriod === undefined) {
                return;
            }
            if (restPeriod.nightRestBreakCountRequired && restPeriod.consecutiveNightRestBreakCountRequired) {
                checkForNightRestBreak(state, restPeriod);
            }
            // Start counting qualified rest segments base on current rest duration
            if (restPeriod.restMinimum && restPeriod.restSegments && state.duration >= restPeriod.restMinimum) {
                let numberOfSegment = Math.floor(state.duration / restPeriod.restMinimum);
                for (let i = 0; i < numberOfSegment; i++) {
                    let restSegment: IRestSegment = {
                        startDate: new Date(state.currentDate.getTime() + i * restPeriod.restMinimum),
                        toDate: new Date(state.currentDate.getTime() + (i + 1) * restPeriod.restMinimum)
                    };
                    restPeriod.restSegments.push(restSegment);
                }
            }
            // Reset current TimePeriod by moving startDate forward
            if (isTimePeriodReset(restPeriod)) {
                if (restPeriod.restMinimum && restPeriod.countRequired && restPeriod.restSegments !== undefined) {
                    if (restPeriod.countRequired > 1) {
                        let firstValidRestSegmentIndex = restPeriod.restSegments.length - restPeriod.countRequired;
                        state.workRestCounts[timePeriod].startDate = restPeriod.restSegments[firstValidRestSegmentIndex].toDate;
                        state.workRestCounts[timePeriod].toDate = new Date(
                            restPeriod.restSegments[firstValidRestSegmentIndex].toDate.getTime() + timePeriod
                        );
                        restPeriod.restSegments = restPeriod.restSegments.slice(restPeriod.restSegments.length - restPeriod.countRequired + 1);
                    } else {
                        restPeriod.restSegments = [];
                        state.workRestCounts[timePeriod].startDate = state.nextDate;
                        state.workRestCounts[timePeriod].toDate = new Date(state.nextDate.getTime() + timePeriod);
                    }
                }
                if (restPeriod.nightRestBreakCount !== undefined && restPeriod.consecutiveNightRestBreakCount !== undefined) {
                    restPeriod.nightRestBreakCount = 0;
                    restPeriod.consecutiveNightRestBreakCount = 0;
                }
                if (workPeriod) {
                    workPeriod.workCount = 0;
                }
            }
        });
    },
    // Driver may use WorkExemption once every Day
    countWorkExemptionTime = (state: IEwdState) => {
        state.consecutiveRestCount = 0;

        // If dummy log = WorkExemption, there can be two in a row
        if (state.workExemptionCount > 0) {
            state.workExemptionCount += state.duration;
        } else if (
            state.currentDate.getDate() > state.lastExemptionDate.getDate() ||
            state.currentDate.getTime() - state.lastExemptionDate.getTime() > DAYS
        ) {
            state.lastExemptionDate = state.currentDate;
            state.workExemptionCount += state.duration;
        } else {
            let violation: IViolation = {
                fromDate: state.currentDate,
                toDate: state.nextDate,
                logId: state.current.id,
                type: "EwdWorkExemption"
            };
            state.violations.push(violation);
        }

        // WorkExemption may not exceed 1 HOUR
        if (state.workExemptionCount > 1 * HOURS) {
            let violation: IViolation = {
                fromDate: new Date(state.lastExemptionDate.getTime() + 1 * HOURS),
                toDate: state.nextDate,
                logId: state.current.id,
                type: "EwdWorkExemption"
            };
            state.violations.push(violation);
            return;
        }

        return;
    },
    getStartingState = (dutyStatusLogs: any[], ruleset: IHosRuleset): IEwdState => {
        let state: IEwdState = {
            logs: dutyStatusLogs,
            config: ruleset.config,
            timePeriodList: [] as number[],
            current: undefined,
            next: undefined,
            currentDate: new Date(0),
            nextDate: new Date(0),
            duration: 0,
            violations: [],
            workRestCounts: {},
            lastExemptionDate: new Date(0),
            lastNightRestBreakDate: new Date(0),
            workExemptionCount: 0,
            consecutiveRestCount: 0
        };

        // Setting up workRestCount for each timePeriod base on the ruleset config
        state.config.workRestConfig &&
            state.config.workRestConfig.forEach(workRestConfig => {
                let timePeriod = workRestConfig.timePeriod;
                state.timePeriodList.push(timePeriod);
                let workPeriod: IEwdWorkPeriod = {};
                let restPeriod: IEwdRestPeriod = {};
                let workPeriodConfig: IEwdRulesetWorkPeriodConfig | undefined = workRestConfig.workPeriod;
                let restPeriodConfig: IEwdRulesetRestPeriodConfig | undefined = workRestConfig.restPeriod;
                if (workPeriodConfig && workPeriodConfig.workMaximum) {
                    workPeriod = {
                        workCount: 0,
                        workMaximum: workPeriodConfig.workMaximum
                    };
                }
                if (restPeriodConfig) {
                    if (restPeriodConfig.restMinimum && restPeriodConfig.countRequired) {
                        restPeriod = {
                            restSegments: [],
                            restMinimum: restPeriodConfig.restMinimum,
                            countRequired: restPeriodConfig.countRequired
                        };
                    }
                    if (restPeriodConfig && restPeriodConfig.nightRestBreakCount) {
                        restPeriod.nightRestBreakCount = 0;
                        restPeriod.nightRestBreakCountRequired = restPeriodConfig.nightRestBreakCount;
                    }
                    if (restPeriodConfig && restPeriodConfig.consecutiveNightRestBreakCount) {
                        restPeriod.consecutiveNightRestBreakCount = 0;
                        restPeriod.consecutiveNightRestBreakCountRequired = restPeriodConfig.consecutiveNightRestBreakCount;
                    }
                }
                let workRestCount: IWorkRestCount = {
                    startDate: dutyStatusLogs[0].dateTime,
                    toDate: new Date(dutyStatusLogs[0].dateTime.getTime() + timePeriod),
                    workPeriod: workPeriod,
                    restPeriod: restPeriod
                };
                state.workRestCounts[timePeriod] = workRestCount;
            });
        state.timePeriodList = state.timePeriodList.sort((a, b) => {
            return a - b;
        });
        return state;
    };

export function EwdCalculator(dummyLogDate: Date | undefined, dutyStatusLogs: any[], userHosRuleSet: IHosRuleset): ICalculationResult {
    let validationResult: IValidation = {
        violations: [],
        ewdAvailability: [],
        cycleSummaries: [],
        availability: getDefaultAvailability("None")
    };
    if (dutyStatusLogs.length <= 0) {
        return { validation: validationResult, state: undefined };
    }
    let state: IEwdState = getStartingState(dutyStatusLogs, userHosRuleSet);
    addDummyLog(state, dummyLogDate);
    let dummyLogCount = state.logs.filter(log => log.dummyLog === true).length;
    if (dummyLogCount < 1) {
        dummyLogCount = 1;
    }

    for (let index = 0; state.logs.length - dummyLogCount > index; index++) {
        state.current = state.logs[index];
        state.next = state.logs[index + 1];
        state.currentDate = state.current.dateTime;
        state.nextDate = state.next.dateTime;
        state.duration = state.nextDate.getTime() - state.currentDate.getTime();

        switch (state.current.status) {
            case "Work":
                countWorkTime(state);
                break;
            case "Rest":
                countRestTime(state);
                break;
            case "WorkExemption":
                countWorkExemptionTime(state);
                break;
            default:
                break;
        }
        calculateViolation(state);
    }
    calculateAvailability(state, validationResult);
    validationResult.violations = state.violations;
    state.next && (validationResult.currentDutyStatus = state.next.status);
    return { validation: validationResult, state: undefined };
}
